import { validateEmail, validateName } from '@tkmp/rfq/utils';
import { CtaButton } from '@tkww/orion-web-ds-action';
import { Input } from '@tkww/orion-web-ds-input';
import { TextBody } from '@tkww/orion-web-ds-text';
import React, { useState } from 'react';
import type { ContactFormData } from '../../../../types';
import { useTrackContactStepSubmit } from '../../../hooks/useTrackContactStepSubmit';
import Styles from './styles.scss';

type OrionInputChangeEvent = {
	target: {
		value: string;
	};
};

export interface ContactStepProps {
	contactData: ContactFormData;
	onChange: (data: ContactFormData) => void;
	onSubmit: () => void;
	isSubmitting: boolean;
	buttonLabel?: string;
	loadingText?: string;
}

export const ContactStep = ({
	contactData,
	onChange,
	onSubmit,
	isSubmitting,
	buttonLabel = 'Unlock estimate',
	loadingText = 'Unlocking estimate...',
}: ContactStepProps) => {
	const [errors, setErrors] = useState<
		Partial<Record<keyof ContactFormData, string>>
	>({});

	const trackContactStepSubmit = useTrackContactStepSubmit();

	const handleChange =
		(field: keyof ContactFormData) => (e: OrionInputChangeEvent) => {
			setErrors((prev) => ({ ...prev, [field]: undefined }));
			onChange({ ...contactData, [field]: e.target.value });
		};

	const getFieldError = (field: keyof ContactFormData): string | undefined => {
		const value = contactData[field];
		if (field === 'firstName' || field === 'lastName')
			return validateName(value.trim()) || undefined;
		if (field === 'email') return validateEmail(value.trim()) || undefined;
		return undefined;
	};

	const validateField = (field: keyof ContactFormData) => (): void => {
		const error = getFieldError(field);
		setErrors((prev) => ({ ...prev, [field]: error }));
	};

	const isValid = () =>
		!validateName(contactData.firstName.trim()) &&
		!validateName(contactData.lastName.trim()) &&
		!validateEmail(contactData.email.trim());

	const validateAll = () => {
		const newErrors: Partial<Record<keyof ContactFormData, string>> = {};
		const fields: (keyof ContactFormData)[] = [
			'firstName',
			'lastName',
			'email',
		];
		for (const field of fields) {
			const error = getFieldError(field);
			if (error) newErrors[field] = error;
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!validateAll()) return;
		trackContactStepSubmit();
		onSubmit();
	};

	return (
		<div className={Styles.contactStep} data-testid="contact-step">
			<div className={Styles.header}>
				<TextBody size="xl" bold>
					Your estimate from The Knot is ready—share your info with the venue to
					unlock
				</TextBody>
				<TextBody size="sm" color="subtle-dark">
					This will start a conversation with the venue. They’ll be able to talk
					next steps and give you an exact price.
				</TextBody>
			</div>
			<div className={Styles.fieldsArea}>
				<div className={Styles.nameRow}>
					<Input
						label="First name"
						name="firstName"
						type="text"
						value={contactData.firstName}
						onChange={handleChange('firstName')}
						onBlur={validateField('firstName')}
						state={errors.firstName ? 'invalid' : 'neutral'}
						subText={errors.firstName}
						required
						data-testid="contact-first-name-input"
					/>
					<Input
						label="Last name"
						name="lastName"
						type="text"
						value={contactData.lastName}
						onChange={handleChange('lastName')}
						onBlur={validateField('lastName')}
						state={errors.lastName ? 'invalid' : 'neutral'}
						subText={errors.lastName}
						required
						data-testid="contact-last-name-input"
					/>
				</div>
				<Input
					label="Email"
					name="email"
					type="email"
					value={contactData.email}
					onChange={handleChange('email')}
					onBlur={validateField('email')}
					state={errors.email ? 'invalid' : 'neutral'}
					subText={errors.email}
					required
					data-testid="contact-email-input"
				/>
			</div>
			<div className={Styles.footer}>
				<TextBody
					size="xs"
					asTyped
					color="subtle-dark"
					className={Styles.policyText}
				>
					By clicking &quot;Unlock estimate,&quot; you accept our{' '}
					<a
						href="https://www.theknot.com/terms-of-use/"
						target="_blank"
						rel="noopener noreferrer"
					>
						Terms of Use
					</a>{' '}
					and agree to The Knot creating an account for you. Your messages may
					be monitored for quality, safety, and security according to our{' '}
					<a
						href="https://www.theknot.com/legalhub/content-moderation-policy/"
						target="_blank"
						rel="noopener noreferrer"
					>
						Acceptable Content Policy
					</a>
					. See our{' '}
					<a
						href="https://www.theknot.com/privacy-policy/"
						target="_blank"
						rel="noopener noreferrer"
					>
						Privacy Policy
					</a>{' '}
					to learn how we handle your data.
				</TextBody>
				<CtaButton
					className={Styles.ctaButton}
					styleType="primary"
					size="lg"
					onClick={handleSubmit}
					disabled={!isValid() || isSubmitting}
					isLoading={isSubmitting}
					loadingText={loadingText}
					data-testid="contact-submit-button"
				>
					{buttonLabel}
				</CtaButton>
			</div>
		</div>
	);
};
